export type ApiErrorKind = "network" | "http" | "business" | "timeout" | "unknown";

/**
 * 백엔드 공통 에러 응답 shape.
 * `/auth/**` 등은 `{ errorCode, message, errors }` 형태로 응답한다.
 * (일부 레거시 응답은 `code` 를 쓰므로 alias 로 함께 수용)
 * `detail`: 탈퇴/정지/차단 안내 등 일부 errorCode 에만 채워지는 구조화 메타.
 */
export type BackendErrorResponse = {
  errorCode?: string;
  code?: string;
  message?: string;
  errors?: Record<string, string | string[]> | null;
  detail?: Record<string, unknown> | null;
};

type ApiErrorOptions = {
  kind?: ApiErrorKind;
  status?: number;
  code?: string;
  errorCode?: string;
  errors?: Record<string, string | string[]>;
  /** 탈퇴/정지/차단 등 일부 errorCode 에만 채워지는 구조화 메타 (§4-1 detail) */
  detail?: Record<string, unknown> | null;
  response?: unknown;
  cause?: unknown;
};

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status?: number;
  /** 레거시 호환 alias. 신규 코드는 errorCode 를 사용한다. */
  readonly code?: string;
  /** 백엔드 머신 판별용 에러 코드 (errorCode 우선, 없으면 code) */
  readonly errorCode?: string;
  /** validation 에러 상세 ({ 필드명: 메시지 }) */
  readonly errors?: Record<string, string | string[]>;
  /** 탈퇴/정지/차단 등 일부 errorCode 에만 채워지는 구조화 메타 (§4-1 detail) */
  readonly detail?: Record<string, unknown> | null;
  /** 파싱된 raw 응답 본문 (디버깅/추가 분기용) */
  readonly response?: unknown;

  constructor(message: string, options: ApiErrorOptions = {}) {
    super(message);
    this.name = "ApiError";
    this.kind = options.kind ?? "unknown";
    this.status = options.status;
    this.code = options.code;
    this.errorCode = options.errorCode;
    this.errors = options.errors;
    this.detail = options.detail;
    this.response = options.response;
    if (options.cause !== undefined) {
      (this as { cause?: unknown }).cause = options.cause;
    }
  }
}

export const isApiError = (error: unknown): error is ApiError =>
  error instanceof ApiError;

/**
 * 순수 전송 실패 여부 — 서버 응답에 도달하지 못했거나(network/timeout) 응답을 해석하지
 * 못한(unknown) 경우. http(서버 errorCode 보유)·business(success:false)는 제외한다.
 * "전송 실패 시 해당 과정 첫 step 리셋" 정책의 판별자로 사용한다.
 */
export const isTransportError = (error: unknown): error is ApiError =>
  error instanceof ApiError &&
  (error.kind === "network" ||
    error.kind === "timeout" ||
    error.kind === "unknown");

/**
 * 일시 장애(network/timeout) — 재연결로 결과가 바뀔 수 있는 실패. unknown(응답 해석 실패)은 제외.
 * 쿼리 retry 정책(queryClient)과 오류 조회 자동 복구(queryRecovery)가 공유하는 판별자다.
 */
export const isTransientApiError = (error: unknown): error is ApiError =>
  error instanceof ApiError &&
  (error.kind === "network" || error.kind === "timeout");

/**
 * 게이트웨이/인프라 5xx(502/503/504) — 서버가 HTTP 응답은 줬지만 CODEF 왕복 결과가 불확정한 경우.
 * isTransportError("서버 무응답" 계약)는 넓히지 않고 별도로 둔다. 호출부에서 transport 와 OR 하여
 * "세션 불확정 → 해당 과정 첫 step 리셋" 정책을 동일하게 적용한다 (#272).
 * ⚠️ 각 status 를 개별 비교한다. `status === 502 || 503 || 504` 축약은 503/504 가 truthy 라 모든
 * http 에러에 true 가 되므로 금지.
 */
export const isSessionIndeterminateInfraError = (
  error: unknown,
): error is ApiError =>
  isApiError(error) &&
  error.kind === "http" &&
  (error.status === 502 || error.status === 503 || error.status === 504);

export const toErrorMessage = (error: unknown, fallback = "요청 중 오류가 발생했습니다."): string => {
  if (error instanceof ApiError) return error.message || fallback;
  if (error instanceof Error) return error.message || fallback;
  return fallback;
};

/**
 * 데이터 로드 실패의 화면 표시 유형 — Figma «오류 케이스» 3유형 + 사번 미등록 예외 (#389).
 * - employee-number-not-set: 사번 미등록(400 EMPLOYEE_NUMBER_NOT_SET) — 재시도로 풀리지 않음, 서버 message 노출
 * - forbidden: 하위 조직원 조회 권한 없음(403 SUBORDINATE_ACCESS_DENIED, #391) — 재시도로 풀리지 않음, 서버 message 노출(부제형 재사용)
 * - rate-limit: 요청 제한(429 RATE_LIMIT_EXCEEDED)
 * - offline: 네트워크 끊김·타임아웃(kind network | timeout)
 * - remote: 그 외 전부(IMS_REMOTE_ERROR·5xx·INVALID_REQUEST·business·unknown·비-ApiError) — 유일하게 재시도 버튼 노출
 */
export type LoadErrorKind =
  | "employee-number-not-set"
  | "forbidden"
  | "rate-limit"
  | "offline"
  | "remote";

/**
 * 429 판별 — errorCode 우선, apiClient 가 status 로만 표면화하는 경우(useInviteCodeRegister 선례) 대비 status 도 본다.
 */
export const isRateLimitError = (error: unknown): boolean =>
  isApiError(error) &&
  (error.errorCode === "RATE_LIMIT_EXCEEDED" || error.status === 429);

/**
 * 로드 실패를 화면 유형으로 분류한다. 판별 순서 고정: 사번 미등록 → 403 접근 거부(forbidden) → 429 → network/timeout → 폴백(remote).
 * isTransportError 는 unknown 을 포함하므로 재사용하지 않는다(unknown 은 폴백=remote).
 */
export const classifyLoadError = (error: unknown): LoadErrorKind => {
  if (isApiError(error)) {
    if (error.errorCode === "EMPLOYEE_NUMBER_NOT_SET") return "employee-number-not-set";
    if (error.errorCode === "SUBORDINATE_ACCESS_DENIED") return "forbidden";
    if (isRateLimitError(error)) return "rate-limit";
    if (error.kind === "network" || error.kind === "timeout") return "offline";
  }
  return "remote";
};
